/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import {
  ValidatedResult,
  useBulkOnboardEmailsMutation,
  useValidateImportFileMutation,
} from '@api/bulk-onboarding-import';
import { ImportFileDropzone } from '@components/files/ImportFileDropZone';
import Loader from '@components/loader';
import { useSiteAlert } from '@hooks/useSiteAlert';
import G17Client from '@services/G17Client';
import { useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { ReviewData, ReviewDataProps } from './ReviewData';
import { BulkOnboardingImportSteps } from './utils';
import '../../../css/common/import-export-template.scss';

const DownLoadTemplateStep = ({ onClick }: { onClick: () => Window | null }) => {
  return (
    <div className='d-flex'>
      <span>Download the </span>
      <Button size='sm' color='link' className='p-0 mx-1 border-0' onClick={onClick}>
        Excel import file template
      </Button>
      <span> and fill in with your data.</span>
    </div>
  );
};

const ReviewDataStep = (props: ReviewDataProps) => {
  if (props.validatedData.length) {
    return <ReviewData {...props} />;
  }

  return <p>No valid users found for import</p>;
};

const Step = ({ step }: { step: number }) => <div className='step'>{step}</div>;

const CompletedProcess = () => (
  <div className='text-ThemeSuccessMedium'>
    <i className='fa fa-check-circle text-ThemeSuccessMedium mr-3' />
    Done
  </div>
);

const defaultValidatedState: ValidatedResult = {
  validatedData: [],
  isUserLimitExceeded: false,
};

export const BulkOnboardingImport = ({ initiativeId, userLimit }: { initiativeId: string; userLimit: number }) => {
  const [validateImportFile, { isLoading: isUploading }] = useValidateImportFileMutation();
  const [bulkOnboardEmails, { isLoading: isProcessing }] = useBulkOnboardEmailsMutation();

  const { addSiteAlert, addSiteError } = useSiteAlert();
  const [data, setData] = useState<ValidatedResult>(defaultValidatedState);
  const [file, setFile] = useState<File | undefined>();
  const validRowCount = useMemo(
    () => (data ? data.validatedData.filter((row) => row.validPermissions.length) : []),
    [data]
  ).length;

  const isLoading = isUploading || isProcessing;
  const canSubmit = !isLoading && !data.isUserLimitExceeded && validRowCount > 0;

  const onFilesAdded = (files: File[]) => {
    const inputFile = files[0];
    if (!inputFile) {
      setData(defaultValidatedState);
      return;
    }
    setFile(inputFile);
    validateImportFile({ initiativeId, data: { file: inputFile } })
      .unwrap()
      .then((result) => {
        setData(result);
      })
      .catch((e) => {
        addSiteError(e);
      });
  };

  const onDownLoadTemplate = () => window.open(G17Client.getBulkOnboardingImportTemplate(initiativeId));

  const handleImport = () => {
    if (!file) {
      return;
    }
    bulkOnboardEmails({ initiativeId, data: { file: file } })
      .unwrap()
      .then(() => {
        addSiteAlert({ content: `Invite ${validRowCount} users successfully` });
        setFile(undefined);
        setData(defaultValidatedState);
      })
      .catch((e) => {
        addSiteError(e);
      });
  };

  return (
    <div>
      {isLoading ? <Loader /> : null}
      <div className='mt-4 import-export-container'>
        <div className='steps text-ThemeTextMedium'>
          <h5>
            <Step step={1} />
            {BulkOnboardingImportSteps.DownLoadTemplate}
          </h5>
          {file ? <CompletedProcess /> : <DownLoadTemplateStep onClick={onDownLoadTemplate} />}
          <h5 className='mt-4'>
            <Step step={2} />
            {BulkOnboardingImportSteps.UploadFile}
          </h5>
          {file ? (
            <CompletedProcess />
          ) : (
            <ImportFileDropzone
              onFilesAdded={onFilesAdded}
              importFile={undefined}
              dropText='Attach template by dragging and dropping, or'
            />
          )}
          <h5 className='mt-4'>
            <Step step={3} />
            {BulkOnboardingImportSteps.ReviewData}
          </h5>
          {file && !isUploading ? (
            <ReviewDataStep {...data} validRowCount={validRowCount} userLimit={userLimit} />
          ) : (
            <p>Once you have uploaded your file, a confirmation screen will be displayed for review.</p>
          )}
          <div className='mt-4 text-right'>
            <Button disabled={!canSubmit} onClick={handleImport}>
              Invite users
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
